import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMobileNumber, handleLoginSuccess } from '../../services/appFunctions';
import { IOtpPayload, IVerifyOtpPayload } from '../../services/auth/interfaces';
import { parseSendOtpResponse, parseVerifyOtpResponse } from '../../services/auth/otpResponse';
import { isValidNationalPhoneDigits, sanitizeNationalPhoneInput } from '../../utils/phoneValidation';
import OtpCodeInput from './OtpCodeInput';
import SelectCountry from './SelectCountry';

const OTP_LENGTH = 4;

type OtpStep = 'phone' | 'otp';

export interface OTPModalProps {
    isOpen: boolean;
    handleClose: () => void;
    sendOtp: (payload: IOtpPayload) => Promise<unknown>;
    verifyOtp: (payload: IVerifyOtpPayload) => Promise<unknown>;
}

function useOtpModalStyles(theme: Theme) {
    return useMemo(() => {
        const rowGap = theme.spacing(1.5);
        const inputHeight = theme.spacing(7);
        const accentBorder = `2px solid ${theme.palette.primary.main}`;

        const modalPaper: SxProps<Theme> = {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 450,
            width: { xs: 300, sm: 325, md: 450 },
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[24],
            p: 3,
            borderRadius: theme.shape.borderRadius * 1.5,
            border: accentBorder
        };

        const closeButton: SxProps<Theme> = {
            position: 'absolute',
            right: theme.spacing(2),
            top: theme.spacing(1.5),
            color: 'text.secondary'
        };

        const phoneRow: SxProps<Theme> = {
            display: 'flex',
            alignItems: 'stretch',
            gap: rowGap,
            mt: 1
        };

        const underlineOff: SxProps<Theme> = {
            '& .MuiInput-underline:before': { borderBottom: 'none' },
            '& .MuiInput-underline:after': { borderBottom: 'none' },
            color: 'text.secondary',
            height: inputHeight,
            alignSelf: 'center'
        };

        const phoneInputShell: SxProps<Theme> = {
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'action.hover',
            borderRadius: theme.shape.borderRadius * 1.5,
            px: 1,
            flex: 1,
            minWidth: 0
        };

        const sendButton: SxProps<Theme> = {
            minWidth: theme.spacing(7),
            height: inputHeight,
            alignSelf: 'center'
        };

        const errorText: SxProps<Theme> = {
            mt: 2,
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
        };

        return {
            modalPaper,
            closeButton,
            phoneRow,
            underlineOff,
            phoneInputShell,
            sendButton,
            errorText
        };
    }, [theme]);
}

interface PhoneNumberFieldProps {
    value: string;
    onChange: (digits: string) => void;
    countryPrefix: string;
    onCountryPrefixChange: (prefix: string) => void;
    inputSx: SxProps<Theme>;
    shellSx: SxProps<Theme>;
}

const PhoneNumberField = React.memo(function PhoneNumberField(props: PhoneNumberFieldProps) {
    const { value, onChange, countryPrefix, onCountryPrefixChange, inputSx, shellSx } = props;

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onChange(sanitizeNationalPhoneInput(e.target.value));
        },
        [onChange]
    );

    return (
        <Box sx={shellSx}>
            <SelectCountry selectedCountry={countryPrefix} onCountryChange={onCountryPrefixChange} style={inputSx} />
            <Input sx={inputSx} placeholder="1234567890" disableUnderline onChange={handleChange} value={value} inputProps={{ 'aria-label': 'Phone number' }} />
        </Box>
    );
});

function OTPModal(props: OTPModalProps) {
    const { isOpen, handleClose, sendOtp, verifyOtp } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const sx = useOtpModalStyles(theme);

    const [step, setStep] = useState<OtpStep>('phone');
    const [mobileNum, setMobileNum] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [countryCode, setCountryCode] = useState('+60');
    const [otpError, setOtpError] = useState('');
    const [sendOtpFailed, setSendOtpFailed] = useState(false);
    const [sendOtpLoading, setSendOtpLoading] = useState(false);
    const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);

    const lastAutoVerifyCodeRef = useRef<string | null>(null);
    const verifyInFlightRef = useRef(false);

    const phoneValid = isValidNationalPhoneDigits(mobileNum);

    const formattedNumber = useMemo(() => formatMobileNumber(countryCode, mobileNum), [countryCode, mobileNum]);

    const resetForm = useCallback(() => {
        setMobileNum('');
        setOtpCode('');
        setStep('phone');
        setOtpError('');
        setSendOtpFailed(false);
        setSendOtpLoading(false);
        setVerifyOtpLoading(false);
        lastAutoVerifyCodeRef.current = null;
        verifyInFlightRef.current = false;
    }, []);

    const closeModalAndReset = useCallback(() => {
        resetForm();
        handleClose();
    }, [handleClose, resetForm]);

    const handlePhoneDigitsChange = useCallback((digits: string) => {
        setMobileNum(digits);
        setSendOtpFailed(false);
    }, []);

    const runSendOtp = useCallback(async () => {
        if (!phoneValid || sendOtpLoading) return;
        setSendOtpLoading(true);
        setSendOtpFailed(false);
        try {
            const payload: IOtpPayload = { mobileNumber: mobileNum, mobilePrefix: countryCode };
            const raw = await sendOtp(payload);
            const parsed = parseSendOtpResponse(raw);
            if (parsed.ok) {
                setStep('otp');
                setOtpCode('');
                setOtpError('');
                lastAutoVerifyCodeRef.current = null;
            } else {
                setSendOtpFailed(true);
            }
        } catch {
            setSendOtpFailed(true);
        } finally {
            setSendOtpLoading(false);
        }
    }, [countryCode, mobileNum, phoneValid, sendOtp, sendOtpLoading]);

    const runVerifyOtp = useCallback(async () => {
        if (otpCode.length !== OTP_LENGTH || verifyInFlightRef.current) return;
        verifyInFlightRef.current = true;
        setVerifyOtpLoading(true);
        setOtpError('');
        try {
            const payload: IVerifyOtpPayload = {
                mobileNumber: mobileNum,
                mobilePrefix: countryCode,
                verificationCode: otpCode
            };
            const raw = await verifyOtp(payload);
            const parsed = parseVerifyOtpResponse(raw);
            if (parsed.ok) {
                setSendOtpFailed(false);
                handleLoginSuccess(parsed.user, handleClose);
                setOtpCode('');
                setStep('phone');
                lastAutoVerifyCodeRef.current = null;
            } else {
                setOtpError(t('login_page.otp_failed'));
            }
        } catch {
            setOtpError(t('login_page.otp_failed'));
        } finally {
            verifyInFlightRef.current = false;
            setVerifyOtpLoading(false);
        }
    }, [countryCode, handleClose, mobileNum, otpCode, t, verifyOtp]);

    useEffect(() => {
        if (step !== 'otp') {
            lastAutoVerifyCodeRef.current = null;
            return;
        }
        if (otpCode.length !== OTP_LENGTH) {
            lastAutoVerifyCodeRef.current = null;
            return;
        }
        if (lastAutoVerifyCodeRef.current === otpCode) return;
        lastAutoVerifyCodeRef.current = otpCode;
        void runVerifyOtp();
    }, [otpCode, runVerifyOtp, step]);

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen, resetForm]);

    const handleOtpChange = useCallback((next: string) => {
        setOtpCode(next);
        setOtpError('');
        lastAutoVerifyCodeRef.current = null;
    }, []);

    const dismissOtpError = useCallback(() => {
        setOtpError('');
    }, []);

    return (
        <Modal open={isOpen} onClose={closeModalAndReset} aria-labelledby="otp-modal-title" aria-describedby="otp-modal-description">
            {step === 'phone' ? (
                <Paper sx={sx.modalPaper}>
                    <IconButton type="button" onClick={closeModalAndReset} sx={sx.closeButton} aria-label={String(t('web.close'))} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography id="otp-modal-title" variant="h2" sx={{ fontWeight: 700, fontSize: { xs: 28, sm: 36 }, pr: 4 }}>
                        {t('login_page.login')}
                    </Typography>
                    <Typography id="otp-modal-description" variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                        {t('login_page.otp_subheader')}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={sx.phoneRow}>
                        <PhoneNumberField
                            value={mobileNum}
                            onChange={handlePhoneDigitsChange}
                            countryPrefix={countryCode}
                            onCountryPrefixChange={setCountryCode}
                            inputSx={sx.underlineOff}
                            shellSx={sx.phoneInputShell}
                        />
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            disabled={!phoneValid || sendOtpLoading}
                            onClick={() => void runSendOtp()}
                            sx={sx.sendButton}
                            aria-label={String(t('login_page.login_in_with_phone_number'))}
                        >
                            {sendOtpLoading ? (
                                <CircularProgress size={22} color="inherit" aria-hidden />
                            ) : (
                                <EastIcon sx={{ color: !phoneValid ? 'action.disabled' : 'primary.contrastText' }} />
                            )}
                        </Button>
                    </Stack>
                    {sendOtpFailed && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            {t('login_page.otp_number_not_found')}
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper sx={sx.modalPaper}>
                    <IconButton type="button" onClick={closeModalAndReset} sx={sx.closeButton} aria-label={String(t('web.close'))} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography id="otp-modal-title" variant="h2" sx={{ fontWeight: 700, fontSize: { xs: 28, sm: 36 }, pr: 4 }}>
                        {t('login_page.otp_enter_code')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                        {t('login_page.otp_tel_number')} {formattedNumber}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                        <OtpCodeInput value={otpCode} onChange={handleOtpChange} length={OTP_LENGTH} disabled={verifyOtpLoading} />
                    </Box>
                    {otpError ? (
                        <Typography variant="body2" sx={sx.errorText} role="alert">
                            {otpError}
                            <IconButton type="button" size="small" onClick={dismissOtpError} aria-label={String(t('web.close'))} edge="end">
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        </Typography>
                    ) : null}
                    <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" component="span">
                            {t('login_page.otp_not_recieved')}
                        </Typography>
                        <Button
                            type="button"
                            variant="text"
                            size="small"
                            disabled={sendOtpLoading || verifyOtpLoading}
                            onClick={() => void runSendOtp()}
                            sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                            aria-label={String(t('login_page.otp_resend'))}
                        >
                            {t('login_page.otp_resend')}
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Modal>
    );
}

export default React.memo(OTPModal);
